
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { StripeBalance as StripeBalanceType, StripeTransaction, StripePayout } from '@/types/stripe-finances';
import StripeBalanceCard from './StripeBalance';
import TransactionsTable from './TransactionsTable';
import PayoutsTable from './PayoutsTable';

interface FinancialOverviewProps {
  stripeAccountId: string | null;
}

const FinancialOverview: React.FC<FinancialOverviewProps> = ({ stripeAccountId }) => {
  const { t } = useTranslation('finances');
  const { toast } = useToast();
  const [balanceData, setBalanceData] = useState<StripeBalanceType | null>(null);
  const [balanceLoading, setBalanceLoading] = useState<boolean>(true);
  const [balanceError, setBalanceError] = useState<Error | null>(null);
  
  const [transactions, setTransactions] = useState<StripeTransaction[] | null>(null);
  const [transactionsLoading, setTransactionsLoading] = useState<boolean>(true);
  const [transactionsError, setTransactionsError] = useState<Error | null>(null);
  
  const [payouts, setPayouts] = useState<StripePayout[] | null>(null);
  const [payoutsLoading, setPayoutsLoading] = useState<boolean>(true);
  const [payoutsError, setPayoutsError] = useState<Error | null>(null);

  useEffect(() => {
    if (!stripeAccountId) return;
    
    const fetchBalanceData = async () => {
      setBalanceLoading(true);
      setBalanceError(null);
      
      try {
        console.log('Fetching Stripe balance...');
        const { data, error } = await supabase.functions.invoke('get-stripe-balance', {
          method: 'GET'
        });

        if (error) {
          console.error('Stripe balance error:', error);
          throw new Error(`Failed to fetch balance: ${error.message}`);
        }

        console.log('Stripe balance data:', data);
        setBalanceData(data);
      } catch (err) {
        console.error('Error fetching Stripe balance:', err);
        setBalanceError(err instanceof Error ? err : new Error(String(err)));
        toast({
          variant: "destructive",
          title: t('errors.connectionError'),
          description: t('balance.failedToLoad')
        });
      } finally {
        setBalanceLoading(false);
      }
    };
    
    const fetchTransactions = async () => {
      setTransactionsLoading(true);
      setTransactionsError(null);
      
      try {
        console.log('Fetching Stripe transactions...');
        const { data, error } = await supabase.functions.invoke('list-recent-stripe-transactions', {
          method: 'POST',
          body: { limit: 20 }
        });

        if (error) {
          console.error('Stripe transactions error:', error);
          throw new Error(`Failed to fetch transactions: ${error.message}`);
        }

        console.log('Stripe transactions data:', data);
        setTransactions(data);
      } catch (err) {
        console.error('Error fetching Stripe transactions:', err);
        setTransactionsError(err instanceof Error ? err : new Error(String(err)));
        toast({
          variant: "destructive",
          title: t('errors.connectionError'),
          description: t('transactions.failedToLoad')
        });
      } finally {
        setTransactionsLoading(false);
      }
    };
    
    const fetchPayouts = async () => {
      setPayoutsLoading(true);
      setPayoutsError(null);
      
      try {
        console.log('Fetching Stripe payouts...');
        const { data, error } = await supabase.functions.invoke('list-recent-stripe-payouts', {
          method: 'POST',
          body: { limit: 10 }
        });

        if (error) {
          console.error('Stripe payouts error:', error);
          throw new Error(`Failed to fetch payouts: ${error.message}`);
        }

        console.log('Stripe payouts data:', data);
        setPayouts(data);
      } catch (err) {
        console.error('Error fetching Stripe payouts:', err);
        setPayoutsError(err instanceof Error ? err : new Error(String(err)));
        toast({
          variant: "destructive",
          title: t('errors.connectionError'),
          description: t('payouts.failedToLoad')
        });
      } finally {
        setPayoutsLoading(false);
      }
    };

    fetchBalanceData();
    fetchTransactions();
    fetchPayouts();
  }, [stripeAccountId, toast, t]);

  return (
    <div className="space-y-6">
      <StripeBalanceCard
        isLoading={balanceLoading}
        error={balanceError}
        balance={balanceData}
      />
      
      <TransactionsTable
        isLoading={transactionsLoading}
        error={transactionsError}
        transactions={transactions}
      />
      
      <PayoutsTable
        isLoading={payoutsLoading}
        error={payoutsError}
        payouts={payouts}
      />
    </div>
  );
};

export default FinancialOverview;
